import { RequestOptions } from './../../services/base.service';
import { CoreSetting } from 'src/app/pages/settings/settings.service';
import { OverviewFiltersInputDto, SelectStatementFilter, SimulateMultiResult } from './simulate-multi/models/simulate-multi-result.model';
import { QueryFilterParameter } from './../../widgets/filters/models/query-filter-parameter.model';
import { StatusMessageResultDto } from './../general/project.service';
import { DeepDiveMasterTableData } from './../../models/forecast/fc-deepdive-models';
import { FilterInput } from 'src/app/models/filter-objects';
import { Injectable, inject } from "@angular/core";
import { BaseService } from "src/app/services/base.service";
import { firstValueFrom, Observable, of, tap } from "rxjs";
import { DetailedPostingsManualDto } from "src/app/models/detailed-postings-manual-dto";
import { EsoDetailedPostingsChartDto } from "./detailed-postings/eso-detailed-postings.models";
import { SelectStatementResult } from 'src/app/models/select-statement-result.model';
import { SimulateMultiTrainingResult } from './simulate-multi/models/simulate-multi-training-result.model';
import { WebCaches } from 'src/app/enums/webCachesEnum';

@Injectable({
	providedIn: "root",
})
export class EsoService {

	private static readonly REQUEST_TOP_PATH: string = "eso";

	private _baseService: BaseService = inject(BaseService);

	public getEsoObjectProductIds(
		requestOptions?: RequestOptions
	): Observable<string[]> {
		const requestEndpoint: string = "GetEsoObjectProductIds";

		let url = `${EsoService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	private readonly _getStockProjectionRequestEndpoint: string = "GetStockProjectionData";

	public getStockProjectionData(
		filters: QueryFilterParameter[]
	): Observable<StockProjectionDto[]> {
		let url = `eso/${this._getStockProjectionRequestEndpoint}`;

		return this._baseService.post(url, filters);
	}

	public getStockProjectionGridData(
		filters: QueryFilterParameter[]
	): Observable<EsoStockProjectionGridDto> {
		let url = `eso/GetEsoStockProjectionGridData`;

		return this._baseService.post(url, filters);
	}

	getProductionData(
		code: string,
		sourceId: number, requestOptions?: RequestOptions,
		contextId: number = 2
	): Observable<Production[]> {
		code = encodeURIComponent(code);
		const url = `eso/GetDataByProduct`;
		return this._baseService.post(url, { code: code, contextId: contextId, sourceId: sourceId }, requestOptions);
	}

	getProductionDataPm(
		code: string,
		sourceId: number,
		requestOptions?: RequestOptions,
		contextId: number = 2
	): Promise<Production[]> {
		code = encodeURIComponent(code);
		const url = `eso/GetDataByProduct`;
		const observable = this._baseService.post<Production[]>(url, { code: code, contextId: contextId, sourceId: sourceId }, requestOptions);

		// Convert Observable to a Promise using firstValueFrom
		return firstValueFrom(observable);
	}

	getProductSimulatedData(
		code: string,
		safetyStock: number,
		lotSize: number,
		sourceId: number, requestOptions?: RequestOptions,
		contextId: number = 2
	): Observable<StockOutProductCombined> {
		code = encodeURIComponent(code);
		const url = `eso/GetSimulatedDataByProduct`;
		return this._baseService.post(url, { code: code, contextId: contextId, safetyStock: safetyStock, lotSize: lotSize, sourceId: sourceId }, requestOptions);
	}


	getSimulateMultiPopupValues(): Observable<CoreSetting[]> {
		const url = `eso/GetEsoSimulateMultiPopupValues`;

		return this._baseService.get(url);
	}

	getSimulationPeriodType(): Observable<any> {
		const url = `eso/GetSimulationPeriodType`;

		return this._baseService.get(url);
	}

	getYearWeeks(): Observable<DateStandard[]> {
		const url = `eso/GetYearWeeks`;

		return this._baseService.get(url);
	}

	getSequenceGenerationList(): Observable<string[]> {
		const url = `eso/GetSequenceErrorList`;

		return this._baseService.get(url);
	}

	savePeriodDefaults(
		firstPeriod: string,
		firstPeriodFc: string,
		lastPeriod: string,
		lastPeriodFc: string,
		actualSource: string,
		forecastSource: string,
		leadTime: string,
		runValidation: boolean = true
	): Observable<any> {
		const url = `eso/SetNewDefaultPeriods?firstPeriod=${firstPeriod}&firstPeriodFc=${firstPeriodFc}&lastPeriod=${lastPeriod}&lastPeriodFc=${lastPeriodFc}&actualSourceRaw=${actualSource}&forecastSourceRaw=${forecastSource}&runValidation=${runValidation}&leadTime=${leadTime}`;

		return this._baseService.get(url);
	}

	savePeriodDefaultsAndValidate(
		firstPeriod: string,
		firstPeriodFc: string,
		lastPeriod: string,
		lastPeriodFc: string,
		actualSource: string,
		forecastSource: string,
		leadTime: string,
		runValidation: boolean = true,
		productCodes: string[]
	): Observable<any> {
		const url = `eso/SetNewDefaultPeriodsAndValidate?firstPeriod=${firstPeriod}&firstPeriodFc=${firstPeriodFc}&lastPeriod=${lastPeriod}&lastPeriodFc=${lastPeriodFc}&actualSourceRaw=${actualSource}&forecastSourceRaw=${forecastSource}&runValidation=${runValidation}&leadTime=${leadTime}`;

		return this._baseService.post(url, productCodes);
	}

	//TODO: Consider removing contextId.
	public getFutureForecastChartData(
		product: Product, requestOptions?: RequestOptions,
		contextId: number = 2
	): Observable<FutureForecastChartDto[]> {
		const url = `eso/GetFutureForecastChartData?contextId=${contextId}`;

		return this._baseService.post(url, product, requestOptions);
	}

	public getFutureForecastChartDataPm(
		product: Product,
		requestOptions?: RequestOptions,
		contextId: number = 2
	): Promise<FutureForecastChartDto[]> {
		const url = `eso/GetFutureForecastChartData?contextId=${contextId}`;

		// Get the observable from the service call
		const observable = this._baseService.post<FutureForecastChartDto[]>(url, product, requestOptions);

		// Convert the observable to a promise
		return firstValueFrom(observable);
	}

	public getMasterProductData(
		product: Product, requestOptions?: RequestOptions
	): Promise<DeepDiveMasterTableData> {
		const url = `eso/GetMasterProductData`;

		// Get the observable from the service call
		const observable = this._baseService.post<DeepDiveMasterTableData>(url, { code: product.code }, requestOptions);

		// Convert the observable to a promise
		return firstValueFrom(observable);
	}

	//TODO: Consider removing contextId.
	public getLeadTimeChartData(
		product: Product, requestOptions?: RequestOptions,
		contextId: number = 2
	): Observable<LeadTimeChartDto[]> {
		const url = `eso/GetLeadTimeChartData?contextId=${contextId}`;

		return this._baseService.post(url, product, requestOptions);
	}

	public getLeadTimeChartDataPm(
		product: Product,
		requestOptions?: RequestOptions,
		contextId: number = 2
	): Promise<LeadTimeChartDto[]> {
		const url = `eso/GetLeadTimeChartData?contextId=${contextId}`;

		// Get the observable from the service call
		const observable = this._baseService.post<LeadTimeChartDto[]>(url, product, requestOptions);

		// Convert the observable to a promise
		return firstValueFrom(observable);
	}

	public getEsoChartData(
		product: Product,
		requestOptions?: RequestOptions,
		contextId: number = 2
	): Promise<EsoDeepChartDto> {
		const url = `eso/getEsoChartData?contextId=${contextId}`;

		// Get the observable from the service call
		const observable = this._baseService.post<EsoDeepChartDto>(url, product, requestOptions);

		// Convert the observable to a promise
		return firstValueFrom(observable);
	}

	public runProductSimulation(
		code: string,
		contextId: number = 2, requestOptions?: RequestOptions
	): Observable<Production[]> {
		const url = `eso/RunProductSimulation`;
		return this._baseService.post(url, { code: code, contextId: contextId }, requestOptions);
	}

	public cancelProductSimulation(
		simulateMultiResultId: number,
		requestOptions: RequestOptions
	): Observable<StatusMessageResultDto> {
		const url = `eso/CancelProductSimulation?id=${simulateMultiResultId}`;

		return this._baseService.post(
			url,
			{},
			requestOptions
		);
	}

	public cancelProductSimulationTraining(
		simulateMultiTrainingResultId: number,
		requestOptions: RequestOptions
	): Observable<StatusMessageResultDto> {
		const url = `eso/CancelProductSimulationTraining?id=${simulateMultiTrainingResultId}`;

		return this._baseService.post(
			url,
			{},
			requestOptions
		);
	}

	//TODO: Consider removing contextId.
	public saveAssumptions(
		product: Product,
		contextId: number = 2
	): Observable<any> {
		const url = `eso/SaveAssumptions?contextId=${contextId}`;

		return this._baseService.post(url, product);
	}

	//TODO: Consider removing contextId.
	public resetAssumptions(
		product: Product,
		contextId: number = 2
	): Observable<any> {
		const url = `eso/ResetAssumptions?contextId=${contextId}`;

		return this._baseService.post(url, product);
	}

	public runMultipleProductSimulation(settings: MassSimulationDto): Observable<StatusMessageResultDto> {
		const url = `eso/RunMultipleProductSimulation`;

		return this._baseService.post(url, settings);
	}

	public initializeOptimalNumberOfThreadsForMultipleProductSimulations(settings: MassSimulationDto): Observable<StatusMessageResultDto> {
		const url = `eso/InitializeOptimalNumberOfThreadsForMultipleProductSimulations`;

		return this._baseService.post(url, settings);
	}

	getActualSalesOptions(): Observable<EsoSelectValue[]> {
		const url = `eso/GetActualSalesOptions`;

		return this._baseService.get(url);
	}

	getForecastSourceOptions(): Observable<EsoSelectValue[]> {
		const url = `eso/GetForecastSourceOptions`;

		return this._baseService.get(url);
	}

	getLeadTimeOptions(): Observable<EsoSelectValue[]> {
		const url = `eso/GetLeadTimeOptions`;

		return this._baseService.get(url);
	}


	private readonly _getEsoSourcingTreeListDataRequestEndpoint: string = "GetEsoSourcingTreeListData";

	public getEsoSourcingTreeListData(
		productId: string,
		inputLevels: number,
		outputLevels: number
	): Observable<any> {
		const url = `eso/${this._getEsoSourcingTreeListDataRequestEndpoint}New`;
		return this._baseService.post(url, {
			productId: productId,
			inputLevels: inputLevels,
			outputLevels: outputLevels
		});
	}

	public getEsoSourcingGraphData(
		queryData: {
			productId: string,
			topRows: number | null,
			upstreamLevel: number | null,
			downStreamLevel: number | null,
			orderByColumnName: string,
			orderByDirection: 'DESC' | 'ASC',
		},
		requestOptions?: RequestOptions
	): Observable<SourcingGraphNode[]> {
		const requestEndpoint: string = "GetEsoSourcingGraphData";

		let url = `${EsoService.REQUEST_TOP_PATH}/${requestEndpoint}?productId=${queryData.productId}&topRows=${queryData.topRows === null ? '' : queryData.topRows}&upstreamLevel=${queryData.upstreamLevel === null ? '' : queryData.upstreamLevel}&downStreamLevel=${queryData.downStreamLevel === null ? '' : queryData.downStreamLevel}&orderByColumnName=${queryData.orderByColumnName}&orderByDirection=${queryData.orderByDirection}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	private readonly _getAutocompleteDataRequestEndpoint: string = "GetAutocompleteData";

	public getAutocompleteData(): Observable<any> {
		const url = `eso/${this._getAutocompleteDataRequestEndpoint}`;

		return this._baseService.get(url);
	}

	private readonly _selectFirstEsoObjectEndpoint: string = "SelectFirstEsoObject";
	public SelectFirstEsoObject(): Observable<any> {
		const cachedData = localStorage.getItem(WebCaches.EsoFirstObject);
		if (cachedData) {
			return of(JSON.parse(cachedData));
		}
		const url = `eso/${this._selectFirstEsoObjectEndpoint}`;
		return this._baseService.post(url).pipe(
			tap(data => {
				localStorage.setItem(WebCaches.EsoFirstObject, JSON.stringify(data));
			}));
	}


	private readonly _getSourcingAutocompleteDataRequestEndpoint: string = "GetSourcingAutocompleteData";

	public getSourcingAutocompleteData(requestOptions?: RequestOptions): Observable<any> {
		const url = `eso/${this._getSourcingAutocompleteDataRequestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	private readonly _getEsoObjectIdRequestEndpoint: string = "GetEsoObjectId";

	public getEsoObjectId(productId: string): Observable<any> {
		const url = `eso/${this._getEsoObjectIdRequestEndpoint}`;

		return this._baseService.post(url, { id: productId });
	}

	private readonly _getLevelingGridDataRequestEndpoint: string = "GetLevelingGridData";

	public getLevelingGridData(esoObjectIds: number[], requestOptions?: RequestOptions): Observable<{
		Period: string,
		DemandQuantity: number
	}[]> {
		let url = `eso/${this._getLevelingGridDataRequestEndpoint}`;

		return this._baseService.post(url, esoObjectIds, requestOptions);
	}

	private readonly _getLevelingDisplacementRequestEndpoint: string = "GetLevelingDisplacement";

	public getLevelingDisplacement(esoObjectIds: number[], requestOptions?: RequestOptions): Observable<DisplacementData[]> {
		let url = `eso/${this._getLevelingDisplacementRequestEndpoint}`;

		return this._baseService.post(url, esoObjectIds, requestOptions);
	}

	private readonly _upsertLevelingDisplacementRequestEndpoint: string = "UpsertLevelingDisplacement";

	public upsertLevelingDisplacement(upsertLevelingDisplacementDto: UpsertLevelingDisplacementDto, isSplitBasedOnLeveledForecast: boolean): Observable<StatusMessageResultDto> {
		const url = `eso/${this._upsertLevelingDisplacementRequestEndpoint}`;

		upsertLevelingDisplacementDto.IsSplitBasedOnLeveledForecast = isSplitBasedOnLeveledForecast;

		return this._baseService.post(url, upsertLevelingDisplacementDto);
	}

	private readonly _upsertLevelingDisplacementMultipleRequestEndpoint: string = "UpsertLevelingDisplacementMultiple";

	public upsertLevelingDisplacementMultiple(upsertLevelingDisplacementMultipleDto: UpsertLevelingDisplacementMultipleDto, isSplitBasedOnLeveledForecast: boolean): Observable<StatusMessageResultDto> {
		const url = `eso/${this._upsertLevelingDisplacementMultipleRequestEndpoint}`;

		upsertLevelingDisplacementMultipleDto.IsSplitBasedOnLeveledForecast = isSplitBasedOnLeveledForecast;

		return this._baseService.post(url, upsertLevelingDisplacementMultipleDto);
	}

	private readonly _deleteLevelingDisplacementMultipleRequestEndpoint: string = "DeleteLevelingDisplacementMultiple";

	public deleteLevelingDisplacementMultiple(esoObjectIds: number[]): Observable<StatusMessageResultDto> {
		const url = `eso/${this._deleteLevelingDisplacementMultipleRequestEndpoint}`;

		return this._baseService.post(url, esoObjectIds);
	}

	getSlimData(): Observable<Product[]> {
		const url = `eso/GetSlimData`;

		return this._baseService.get(url);
	}

	public getSlimDataFast(requestOptions?: RequestOptions): Observable<string[]> {
		const requestEndpoint: string = "GetSlimDataFast";

		let url = `${EsoService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getSupplyForecastOverviewChartData(
		filters: QueryFilterParameter[],
		groupingField: string = '',
		esoObjectIds?: number[],
		requestOptions?: RequestOptions
	): Observable<SupplyForecastChartDto[]> {
		const url = `eso/GetSupplyForecastOverviewChartData?groupingField=${groupingField}`;

		return this._baseService.post(url, { filters: filters, esoObjectIds: esoObjectIds }, requestOptions);
	}

	getSupplyForecastPeriodList(requestOptions?: RequestOptions): Observable<string[]> {
		const url = `eso/GetSupplyForecastPeriodList`;

		return this._baseService.get(url, requestOptions);
	}

	//TODO: Consider removing contextId.
	public getProduct(
		productName: string,
		scenario: Scenario = new Scenario(),
		asIsScenario: Scenario = new Scenario(), requestOptions?: RequestOptions,
		lotSize: number = null,
		safetyStock: number = null,
		contextId: number = 2
	): Observable<Product> {
		if (scenario == null) {
			scenario = new Scenario();
		}
		if (asIsScenario == null) {
			asIsScenario = new Scenario();
		}
		productName = encodeURIComponent(productName);
		const url = `eso/GetProduct?lotSize=${lotSize}&safetyStock=${safetyStock}`;
		return this._baseService.post(url, { productName: productName, contextId: contextId, scenario, asIsScenario }, requestOptions);
	}

	//TODO: Consider removing contextId.
	public getNetworkProduct(
		options: {
			productName: string,
			scenario?: Scenario,
			lotSize?: number,
			safetyStock?: number,
			contextId?: number,
		},
		requestOptions?: RequestOptions
	): Observable<Product> {
		const requestEndpoint: string = "GetNetworkProduct";

		let url = `${EsoService.REQUEST_TOP_PATH}/${requestEndpoint}?lotSize=${options.lotSize ?? null}&safetyStock=${options.safetyStock ?? null}`;

		return this._baseService.post(
			url,
			{
				Scenario: options.scenario ?? new Scenario(),
				contextId: options.contextId ?? 2,
				productName: options.productName
			},
			requestOptions
		);
	}

	//TODO: Consider removing contextId.
	public getScenario(
		esoObjectId: string, requestOptions?: RequestOptions,
		contextId: number = 3
	): Observable<Scenario[]> {
		const url = `eso/GetScenarios?esoObjectId=${esoObjectId}&contextId=${contextId}`;
		return this._baseService.get(url, requestOptions);
	}

	getDataBySelectedProduct(
		code: string,
		contextId: number = 3
	): Observable<Product[]> {
		const url = `eso/GetDataBySelectedProduct?contextId=${contextId}&code=${code}&actualSource=1&forecastSource=1`;
		return this._baseService.get(url);
	}

	getInputEso(): Observable<Production[]> {
		const url = `eso/GetInputEso`;
		return this._baseService.get(url);
	}

	private readonly _getEsoNetworkDeepDiveCaptionsRequestEndpoint: string = "GetNetworkDeepDiveCaptions";

	public getEsoNetworkDeepDiveCaptions(requestOptions?: RequestOptions): Observable<FilterInput[]> {
		const url = `eso/${this._getEsoNetworkDeepDiveCaptionsRequestEndpoint}`;

		return this._baseService.get(url, requestOptions);
	}

	private readonly _getEsoNetworkDeepDiveProductDetailsRequestEndpoint: string = "GetEsoNetworkDeepDiveProductDetails";

	public getEsoNetworkDeepDiveProductDetails(
		esoObjectId: number,
		requestOptions?: RequestOptions
	): Observable<any> {
		const url = `eso/${this._getEsoNetworkDeepDiveProductDetailsRequestEndpoint}?esoObjectId=${esoObjectId}`;

		return this._baseService.get(url, requestOptions);
	}

	private readonly _getEsoNetworkDeepDiveMultipleProductDetailsRequestEndpoint: string = "GetEsoNetworkDeepDiveMultipleProductDetails";

	public getEsoNetworkDeepDiveMultipleProductDetails(esoObjectIds: number[]): Observable<any> {
		const url = `eso/${this._getEsoNetworkDeepDiveMultipleProductDetailsRequestEndpoint}`;

		return this._baseService.post(url, esoObjectIds);
	}

	getDecisionTreeData(): Observable<DecisionTreeRule[]> {
		const url = `eso/GetDecisionTreeData`;
		return this._baseService.get(url);
	}

	updateDecisionTreeList(data: DecisionTreeRule[]): Observable<any> {
		const url = `eso/UpdateDecisionTreeList`;
		return this._baseService.post(url, data);
	}

	deleteDecisionTreeRule(ruleId: number): Observable<any> {
		const url = `eso/DeleteDecisionTreeRule`;
		return this._baseService.post(url, ruleId);
	}

	runDecisionTreeUpdate(): Observable<any> {
		const url = `eso/UpdateDecisionTreeData`;
		return this._baseService.get(url);
	}

	getEsoSupplyForecastTableData(
		filters: QueryFilterParameter[],
		periodType: string,
		requestOptions?: RequestOptions
	): Observable<any> {
		const url = `eso/GetSupplyForecastTableData?periodType=${periodType}`;
		return this._baseService.post(url, filters, requestOptions);
	}

	createNewDecisionTree(newRuleOrder: number): Observable<any> {
		const url = `eso/CreateNewDecisionTreeRule`;
		return this._baseService.post(url, newRuleOrder);
	}

	getEsoOverviewDataQueryString(
		action: string,
		filterOutpout: QueryFilterParameter[]
	): Observable<any> {
		let url = `eso/GetEsoOverviewData?filters.action=${action}`;
		if (filterOutpout && filterOutpout.length > 0) {
			for (let i = 0; i < filterOutpout.length; i++) {
				const filtersOptions = filterOutpout[i];
				url += `&filters.filters[${i}].fieldName=${filtersOptions.fieldName}&filters.filters[${i}].value=${filtersOptions.value}&filters.filters[${i}].operation=${filtersOptions.operation}`;
			}
		}
		return this._baseService.get(url);
	}

	getEsoOverviewData(
		action: string,
		filterOutpout: QueryFilterParameter[]
	): Observable<any> {
		let url = `eso/GetEsoOverviewData`;
		let item: OverviewFiltersInputDto = new OverviewFiltersInputDto();
		item.Action = action;
		item.Filters = [];
		if (filterOutpout && filterOutpout.length > 0) {
			for (let i = 0; i < filterOutpout.length; i++) {
				const filtersOptions = filterOutpout[i];
				item.Filters.push({ FieldName: filtersOptions.fieldName, Operation: filtersOptions.operation, Value: filtersOptions.value });
			}
		}
		return this._baseService.post(url, item);
	}

	getSupplyForecastFilters(filters: QueryFilterParameter[], periodType: string): Observable<any> {
		let url = `eso/GetSupplyForecastFilters?periodType=${periodType}`;

		return this._baseService.post(url, filters);
	}

	private readonly _getSimulateMultiResultsRequestEndpoint: string = "GetSimulateMultiResults";

	public getSimulateMultiResults(lastAvailableSimulateMultiResultId: number = -1, requestOptions?: RequestOptions): Observable<SimulateMultiResult[]> {
		const url = `eso/${this._getSimulateMultiResultsRequestEndpoint}?lastAvailableSimulateMultiResultId=${lastAvailableSimulateMultiResultId}`;

		return this._baseService.get(url,
			requestOptions);
	}

	private readonly _getSimulateMultiResultRequestEndpoint: string = "GetSimulateMultiResult";

	public getSimulateMultiResult(
		id: number,
		requestOptions?: RequestOptions
	): Observable<SimulateMultiResult> {
		const url = `eso/${this._getSimulateMultiResultRequestEndpoint}?id=${id}`;

		return this._baseService.get(url, requestOptions);
	}

	public getSimulateMultiTrainingResults(lastAvailableSimulateMultiTrainingResultId: number = -1, requestOptions?: RequestOptions): Observable<SimulateMultiTrainingResult[]> {
		const requestEndpoint: string = "GetSimulateMultiTrainingResults";

		let url = `${EsoService.REQUEST_TOP_PATH}/${requestEndpoint}?lastAvailableSimulateMultiTrainingResultId=${lastAvailableSimulateMultiTrainingResultId}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	getEsoDeepDiveGridData(
		action: string,
		filterOutpout: QueryFilterParameter[], opts: RequestOptions
	): Observable<any> {
		let url = `eso/GetEsoDeepDiveGridData?filters.action=${action}`;
		if (filterOutpout && filterOutpout.length > 0) {
			for (let i = 0; i < filterOutpout.length; i++) {
				const filtersOptions = filterOutpout[i];

				url += `&filters.filters[${i}].fieldName=${filtersOptions.fieldName}&filters.filters[${i}].value=${filtersOptions.value}&filters.filters[${i}].operation=${filtersOptions.operation}`;
			}
		}
		return this._baseService.get(url, opts);
	}

	GetEsoOverviewFiltersQuery(filters: QueryFilterParameter[]): Observable<any> {
		let url = `eso/GetEsoOverviewFilters`;

		if (filters && filters.length > 0) {
			for (let i = 0; i < filters.length; i++) {
				const filter = filters[i];
				url +=
					(i === 0 ? "?" : "&") +
					`filters[${i}].fieldName=${filter.fieldName}&filters[${i}].value=${filter.value}&filters[${i}].operation=${filter.operation}`;
			}
		}

		return this._baseService.get(url);
	}

	GetEsoOverviewFilters(filters: QueryFilterParameter[]): Observable<any> {
		let url = `eso/GetEsoOverviewFilters`;
		let items: SelectStatementFilter[] = [];

		if (filters && filters.length > 0) {
			for (let i = 0; i < filters.length; i++) {
				const filter = filters[i];
				items.push({ FieldName: filter.fieldName, Operation: filter.operation, Value: filter.value });
			}
		}

		return this._baseService.post(url, items);
	}

	GetEsoStockProjectionFilters(filters: QueryFilterParameter[]): Observable<any> {
		let url = `eso/GetEsoStockProjectionFilters`;

		return this._baseService.post(url, filters);
	}

	SaveDecisionTreeManuals(records: DecisionTreeManualDto[]) {
		return this._baseService.post("eso/SaveDecisionTreeManuals", records);
	}

	SaveDetailedPostingsManuals(records: DetailedPostingsManualDto[]) {
		return this._baseService.post("eso/SaveDetailedPostingsManuals", records);
	}

	GetDetailedPostingsChartData(productId: string) {
		return this._baseService.post<EsoDetailedPostingsChartDto[]>(
			"eso/GetDetailedPostingsChartData", { id: productId }
		);
	}

	GetDetailedPostingsData(productId: string) {
		return this._baseService.post(
			"eso/GetDetailedPostingsData", { id: productId }
		);
	}
}

export class DisplacementData {
	// EsoObjectId: number;
	public DisplaceFrom: string;
	public DisplaceTo: string;
	public DisplaceQty: number;
	public Count: number;
}

export class UpsertLevelingDisplacementDto {
	public EsoObjectIds: number[];
	public DisplaceFrom: string;
	public DisplaceTo: string;
	public DisplaceQty: number;
	public IsSplitBasedOnLeveledForecast: boolean;
}

export class DisplacementDto {
	public DisplaceFrom: string;
	public DisplaceTo: string;
	public DisplaceQty: number;
}

export class UpsertLevelingDisplacementMultipleDto {
	public EsoObjectIds: number[] = [];
	public Data: DisplacementDto[] = [];
	public IsSplitBasedOnLeveledForecast: boolean;
}

export class SupplyForecastChartDto {
	quantity: number;
	demandQuantity: number;
	groupingField: string;
}

export class SupplyFcBarChartDto {
	quantitySum: number;
	demandQuantitySum: number;
	title: string;
}

export class LeadTimeChartDto {
	leadTime: number;
	standardLt: number;
	uncertainLt: number;
	periodMock: number;
}

export class EsoDeepChartDto {
	public leadTime: LeadTimeChartDto[] = [];
	public futureForecast: FutureForecastChartDto[] = [];
	public dataForProduct: Production[] = [];
}

export class FutureForecastChartDto {
	periodId: string;
	quantity: number;
}

export class DateStandard {
	yearWeek: string;
}

export class SourcingGraphNode {
	productId: string;
	esoObjectId: number;
	esoObjectName: string;
	clientId: number;
	esoObjectGroup: string;
	esoObjectGroup2: string;
	esoObjectGroup3: string;
	esoObjectGroup4: string;
	esoObjectGroup5: string;
	esoObjectGroup6: string;
	esoObjectGroup7: string;
	esoObjectGroup8: string;
	esoObjectGroup9: string;
	esoObjectGroup10: string;
	esoObjectGroup11: string;
	esoObjectGroup12: string;
	esoObjectGroup13: string;
	esoObjectGroup14: string;
	esoObjectGroup15: string;
	esoObjectGroup16: string;
	esoObjectGroup17: string;
	esoObjectGroup18: string;
	esoObjectGroup19: string;
	esoObjectGroup20: string;
	esoObjectGroup21: string;
	esoObjectGroup22: string;
	esoObjectGroup23: string;
	esoObjectGroup24: string;
	esoObjectGroup25: string;
	esoObjectGroup26: string;
	esoObjectGroup27: string;
	esoObjectGroup28: string;
	esoObjectGroup29: string;
	esoObjectGroup30: string;
	esoObjectGroup31: string;
	esoObjectGroup32: string;
	esoObjectGroup33: string;
	esoObjectGroup34: string;
	esoObjectGroup35: string;
	esoObjectGroup36: string;
	esoObjectGroup37: string;
	esoObjectGroup38: string;
	esoObjectGroup39: string;
	esoObjectGroup40: string;
	esoObjectGroup41: string;
	esoObjectGroup42: string;
	esoObjectGroup43: string;
	esoObjectGroup44: string;
	esoObjectGroup45: string;
	esoObjectGroup46: string;
	esoObjectGroup47: string;
	esoObjectGroup48: string;
	esoObjectGroup49: string;
	esoObjectGroup50: string;
	locationName: string;
	endConsumption: boolean;
	capacityId: number;
	dateId: number;
	dDMrpStockStatus: number;
	latitude: number;
	longitude: number;
	esoNetworkInfo: string;
	level: number;
	currentEsoObjectId: number;
	parentEsoObjectId: number;
	cOGSperUnit: number;
	grossProfit: number;
	unitsPerPallet: number;
	palletDayOpex: number;
	calculationRate: number;
	backOrderPercent: number;
	stockOutPenaltyPerUnit: number;
	cOGSperBatch: number;
	unitsPerBatch: number;
	scrapRatio: number;
	leadTimeDays: number;
	lifeCycleDays: number;
	isAutoSimulated?: boolean;
	currentSafetyStock: number;
	analysisStatus: string;
	unplannedProdCost: number;
	stockCtrEsoLine: number;
	stockCtrIssue: string;
	stockCtrPriority: number;
	invDiff: number;
	isAbcScoped: boolean;
	minServiceLevel: number;
	isSequenceSimulation: boolean;
	esoSegmentId?: number;
	stockControllingSegmentId: number;
	esoscope: string;
	worstSequenceLengthDays: number;
	sequencyErrorGenerationId: number;
	mrpConsumptionBackwardDays: number;
	mrpConsumptionForwardDays: number;
	mrpScope: number;
	mrpSafetyStock: number;
	mrpRounding: number;
	mrpPlanInbound: boolean;
	locationId?: number;
	esoMeasure1: number;
	esoMeasure2: number;
	esoMeasure3: number;
	esoMeasure4: number;
	esoMeasure5: number;
	esoSupplyCalendarId: number;
	mrpLeadTimeDays: number;
	mrpUnitPerBatch: number;
	deepDiveScope: number;
	mrpOrder: number;
	startDateId: number;
	endDateId: number;
	planningMethod: string;
	isValid?: boolean;
	esoUserMsgValid: string;
	mrpFreezeDays: number;
	uoM1: number;
	uoM2: number;
	uoM3: number;
	uoM4: number;
	uoM5: number;
	stockStatus: number;
	actualRTM: number;
	forecastRTM: number;
	supplierId: number;
	orderMeasurePerUnit1: number;
	orderMeasurePerUnit2: number;
	orderMeasurePerUnit3: number;
	stockControllingId: number;
	stockValue: number;
	revenueForecast: number;
	esoObjectProductImageGroup1: string;
	esoObjectProductImageGroup2: string;
	mrpPermanentComment: string;
	mrpAction: string;
	lastUpdatedByUserId?: number;
	mrpActionUpdateDate?: Date;
	inputQtyPerOutput: number;
	text: string;
}

export class Sourcing {
	public outputEsoObjectId: number;
	public inputEsoObjectId: number;
	public inputQtyPerOutput: number;
	public esoObjectName: string;
	public ddMrpStockStatus: number;
}

export class MappedSourcingDto {
	dependentProductsCount: number;
	productsInSameGroupCount: number;

	productId: string;

	esoObjectId: number;
	esoObjectName: string;

	clientId: number;

	esoObjectGroup: string;
	esoObjectGroup2: string;
	esoObjectGroup3: string;
	esoObjectGroup4: string;
	esoObjectGroup5: string;
	esoObjectGroup6: string;
	esoObjectGroup7: string;
	esoObjectGroup8: string;
	esoObjectGroup9: string;
	esoObjectGroup10: string;
	esoObjectGroup11: string;
	esoObjectGroup12: string;
	esoObjectGroup13: string;
	esoObjectGroup14: string;
	esoObjectGroup15: string;
	esoObjectGroup16: string;
	esoObjectGroup17: string;
	esoObjectGroup18: string;
	esoObjectGroup19: string;
	esoObjectGroup20: string;
	esoObjectGroup21: string;
	esoObjectGroup22: string;
	esoObjectGroup23: string;
	esoObjectGroup24: string;
	esoObjectGroup25: string;
	esoObjectGroup26: string;
	esoObjectGroup27: string;
	esoObjectGroup28: string;
	esoObjectGroup29: string;
	esoObjectGroup30: string;
	esoObjectGroup31: string;
	esoObjectGroup32: string;
	esoObjectGroup33: string;
	esoObjectGroup34: string;
	esoObjectGroup35: string;
	esoObjectGroup36: string;
	esoObjectGroup37: string;
	esoObjectGroup38: string;
	esoObjectGroup39: string;
	esoObjectGroup40: string;
	esoObjectGroup41: string;
	esoObjectGroup42: string;
	esoObjectGroup43: string;
	esoObjectGroup44: string;
	esoObjectGroup45: string;
	esoObjectGroup46: string;
	esoObjectGroup47: string;
	esoObjectGroup48: string;
	esoObjectGroup49: string;
	esoObjectGroup50: string;

	locationName: string;
	endConsumption: boolean;
	capacityId: number;
	dateId: number;

	ddMrpStockStatus: number;
	// ddMrpStockStatusClass: string;

	latitude: number;
	longitude: number;

	esoNetworkInfo: string;
}

export class MassSimulationDto {
	forecastSource: number;
	actualSource: number;
	leadTime: number;
	firstWeekIdForecast: string;
	lastWeekIdForecast: string;
	lastWeekId: string;
	firstWeekId: string;
	codes: string[];
	simulationName: string;
}

export class EsoSelectValue {
	listId: number;
	listElement: string;
	isDefault: boolean;
}

export class SourcingDto {
	constructor(
		public sourcing: Sourcing,
		public mappedSourcing: MappedSourcingDto
	) { }
}

export class ProductsGroupDto {
	public sourcing: SourcingDto;

	public productsGroups: Array<ProductsGroupDto>;
}

export class ProductsContainerDto {
	public selectedProductSourcing: SourcingDto;

	public leftProductsGroup: ProductsGroupDto;
	public rightProductsGroup: ProductsGroupDto;
}

export class DecisionTreeManualDto {
	productId: string;
	serviceLevelManual: string;
	safetyStockManual: number;
	comment: string;
}

export class Production {
	esoObjectId: string;
	periodId: string;
	stock: number;
	forecast: number;
	forecastError: number;
	forecastClean: number;
	actualRaw: number;
	actualClean: number;
	actualClean2: number;
	periodKey: string;
}

export class FinancialSituationScenario {
	productId: string;
	esoObjectId: number;
	name: string;
	safetyStock: number;
	lotSize: number;
	serviceLevel: number;
	cogs: number;
	inventoryCapital: number;
	inventoryOpex: number;
	stockOut: number;
	scrap: number;
	inventoryUnits: number;
	inventoryAmount: number;

	constructor(data?: Partial<FinancialSituationScenario>) {
		this.productId = data?.productId ?? '';
		this.esoObjectId = data?.esoObjectId ?? 0;
		this.name = data?.name ?? '';
		this.safetyStock = data?.safetyStock ?? 0;
		this.lotSize = data?.lotSize ?? 0;
		this.serviceLevel = data?.serviceLevel ?? 0;
		this.cogs = data?.cogs ?? 0;
		this.inventoryCapital = data?.inventoryCapital ?? 0;
		this.inventoryOpex = data?.inventoryOpex ?? 0;
		this.stockOut = data?.stockOut ?? 0;
		this.scrap = data?.scrap ?? 0;
		this.inventoryUnits = data?.inventoryUnits ?? 0;
		this.inventoryAmount = data?.inventoryAmount ?? 0;
	}
}

export class ScenarioFinancialsToBe {
	productId: string;
	esoObjectId: number;
	name: string;
	safetyStock: number;
	lotSize: number;
	serviceLevel: number;
	cogs: number;
	inventoryCapital: number;
	inventoryOpex: number;
	stockOut: number;
	scrap: number;
	inventoryUnits: number;
	inventoryAmount: number;

	constructor(data?: Partial<ScenarioFinancialsToBe>) {
		this.productId = data?.productId ?? '';
		this.esoObjectId = data?.esoObjectId ?? 0;
		this.name = data?.name ?? '';
		this.safetyStock = data?.safetyStock ?? 0;
		this.lotSize = data?.lotSize ?? 0;
		this.serviceLevel = data?.serviceLevel ?? 0;
		this.cogs = data?.cogs ?? 0;
		this.inventoryCapital = data?.inventoryCapital ?? 0;
		this.inventoryOpex = data?.inventoryOpex ?? 0;
		this.stockOut = data?.stockOut ?? 0;
		this.scrap = data?.scrap ?? 0;
		this.inventoryUnits = data?.inventoryUnits ?? 0;
		this.inventoryAmount = data?.inventoryAmount ?? 0;
	}
}

export interface DotNetTuple<T1, T2> {
	item1: T1;
	item2: T2;
}

export interface ProductWithScenarios {
	product: Product;
	relatedScenarios: DotNetTuple<FinancialSituationScenario, ScenarioFinancialsToBe>[];  // i.e. Array<[FinancialSituationScenario, ScenarioFinancialsToBe]>
	scenarios: Scenario[];
}

export class Product {
	productSuID: number;
	id: number;
	leadTimePeriods: number;
	lifeCyclePeriods: number;
	grossProfitPerUnit: number;
	cogsPerUnit: number;
	cogsPerBatch: number;
	opexPerPalletPerPeriod: number;
	stockOutPenaltyPerUnit: number;
	unitsPerPallet: number;
	unitsPerBatch: number;
	redeliveryRatio: number;
	scrapRatio: number;
	calculationRatio: number;
	firstSaleWeekSuID: number;
	currentSafetyStock: any;
	scale: number;
	unplannedProdCost: number;
	esoObjectGroup2: string;
	esoObjectGroup3: string;
	esoObjectGroup4: string;
	esoObjectGroup5: string;
	minServiceLevel: number;
	lotSize: number;
	code: string;
	cogsAsIs: number;
	cogsToBe: number;
	inventoryCapitalAsIs: number;
	inventoryCapitalToBe: number;
	inventoryOpexAsIs: number;
	inventoryOpexToBe: number;
	stockOutAsIs: number;
	stockOutToBe: number;
	scrapAsIs: number;
	scrapToBe: number;
	inventoryAmountAsIs: number;
	inventoryAmountToBe: number;
	inventoryUnitsAsIs: number;
	inventoryUnitsToBe: number;
	unplannedProductionChangeAsIs: number;
	unplannedProductionChangeToBe: number;
	sequencyErrorGeneration: string;
	inventorySimulation: number;
	inventoryActual: number;
	inventoryCostOfCapital: number;
	inventoryNumOfPallets: number;
	palletsWeeksPerYear: number;
	inventoryOpexCost: number;
	stockOut: number;
	lostSales: number;
	grossProfCurrLotSize: number;
	lostGrossProfit: number;
	penalty: number;
	scrap: number;
	scrapPercents: number;
	costOfScrap: number;
	stockOutSituations: number;
	cost: number;
	periodsWithoutStockOut: number;
	serviceLevelAsIsNew: number;
	secondHeaderName: string;

	cogsDiff: number = 0;
	inventoryCapitalDiff: number = 0;
	inventoryOpexDiff: number = 0;
	stockOutDiff: number = 0;
	unplannedProductionChangeDiff: number = 0;
	scrapDiff: number = 0;

	totalDiff: number = 0;
	totalAsIs: number = 0;
	totalToBe: number = 0;
	totalPercentage: string = '';

	cogsPercentage: string = '';
	inventoryCapitalPercentage: string = '';
	inventoryOpexPercentage: string = '';
	stockOutPercentage: string = '';
	unplannedProductionChangePercentage: string = '';
	scrapPercentage: string = '';

	inventoryAmountDiff: number = 0;
	inventorAmountPercentage: string = '';
	inventoryUnitsDiff: number = 0;
	inventoryUnitsPercentage: string = '';
}

export class GeneralData {
	items: Data[];
	total: number;
}

export class Data {
	index: number;
	esoObjectId: string;
	code: string;
	severity: number;
	userMsg: string;
	isAutoSimulated: boolean;
	esoObjectGroup2: string;
	esoObjectGroup3: string;
	esoObjectGroup4: string;
	esoObjectGroup5: string;
}

export class ActualInventory {
	periodId: string;
	inventoryOpenning: number;
	sales: number;
	invAdjustments: number;
	productionPurchase: number;
}

export class Scenario {
	name: string;
	calculationRatio: number;
	safetyStock: number;
	lotSize: any;
	reorderQty: number;
	isReadOnly: boolean;
	fssId: number;
	potential: number;
	isMaxPotential: boolean;
	serviceLevel: number;
	cogs: number;
	inventoryCapital: number;
	inventoryOpex: number;
	stockOut: number;
	scrap: number;
}
// PercentDifference :number;
export class ScenarioTableCalculetedFields {
	safetyStockPercentDifference: number;
	lotSizePercentDifference: number;
	serviceLevelPercentDifference: number;
	cogsPercentDifference: number;
	inventoryCapitalPercentDifference: number;
	inventoryOpexPercentDifference: number;
	stockOutPercentDifference: number;
	scrapPercentDifference: number;
	total: number[] = [];
}

export class ForecastError {
	periodId: string;
	forecast: number;
	forecastError: number;
}

export class ForecastAndActual {
	periodId: string;

	actualRaw: number;
	actualClean: number;

	forecast: number;
	forecastClean: number;
}

export class StockOutProductCombined {
	stockOuts: StockOut[];
	product: Product;
}

export class StockOut {
	production: number;
	scrap: number;
	sales: number;
	weekNumber: number;
	inventory: number;
	chartIndex: number;
}

export class TempClass {
	esoObjectId: string;
	code: string;
	index: number;
}

export class DecisionTreeRule {
	ruleId: number;
	ruleOrder: number;
	ruleName: string;
	ruleJoin: string;
	ruleWhere: string;
	adjustment: string;
	adjustmentDescription: string;
	applyToAll: boolean;
	serviceLevelAdj: boolean;
	active: boolean;
	validationAction: string;
	ruleDescription: string;

	decisionTrees: DecisionTree[];

	// Additional properties for the page. Does not belong to the original model
	selectedAdjustment: string;
	stocks: Stock[];
}

export class DecisionTree {
	decisionTreeName: string;
	stockValueFinal: number;
	stockValueCurrent: number;
	stockValueStatus: number;
	count: number;
	serviceLevelFinal: number;
}

export class Stock {
	DataName: string;
	DataValue: number;
}

export class StockProjectionDto {
	StockActual: number;
	StockProjected: number;
	StockActualSimulation: number;
	SafetyStock: number;
	UnitsPerBatch: number;
	StockKeyFigure1: number;
	StockKeyFigure2: number;
	StockKeyFigure3: number;
	period: string;
}

export class EsoStockProjectionGridDto {
	gridData: SelectStatementResult;
}

export class StockChart {
	DataName: string;
	DataValue: number;
}

export class StockChartData {
	Name: string;
	Value: number;
}
